import {
  Box,
  Button,
  Dialog,
  Fade,
  Grid2,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import {
  AccessTime,
  Apartment,
  Assistant,
  Devices,
  IntegrationInstructions
} from "@mui/icons-material";
import { Datepicker, locale } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { AutomatedBookingRoutine } from "../../../features/Booking-Form/partials/AutomatedBookingRoutinePartial/automated-booking-routine.partial";
import { LocationInventory } from "../../../features/FloorManager/typings/location-inventory";
import { setRequireSettings, setSettings } from "../../../features/Login/slices/login.slice";
import { getLocationsWithoutMobileWorking } from "../../../utils/axios-requests";
import LocationCombo from "../../Pickers/location-combo.component";
import { homeOfficePolicyText, WorkingDays } from "../utils/booking.utils";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { RootState } from "../../../app/rootReducer";
import { useHomeOffice } from "./Hooks/useHomeOffice";
import { useRemoteSetUserSettings } from "../../../hooks/Remote/User/useRemoteSetUserSettings";
import CalendarSyncIntegrationBooking from "../../Settings/calendarSync-integration-booking.component";
import { DialogHeaderWithClose } from "../../Dialog/DialogHeaderWithClose.component";
import { SeeditDialogActions } from "../../Dialog/SeeditDialogActions.component";
import { SeeditDialogContent } from "../../Dialog/SeeditDialogContent.component";
import { InfoIconWithTooltip } from "../../Title/InfoIcon";

export default function BookingSettingsView() {
  const {
    userInformation: { automatedBookingActivated, sub },
    settings
  } = useSelector((state: RootState) => state.login);
  const { workplaceBookingEnabled, bookingAssistenceActive, calendarSyncPossible } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const matches = useMediaQuery("(max-width:1000px)");
  const flexWrap = matches ? "wrap" : "nowrap";

  const automatedBookingValue = automatedBookingActivated ? "auto" : "";

  const [automatedOpen, setAutomatedOpen] = useState(false);
  const [calendarSyncOpen, setCalendarSyncOpen] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [locations, setLocations] = useState<LocationInventory[]>([]);
  const [toggle, setToggle] = React.useState(automatedBookingValue);

  const { timeFormat } = useTimeFormat();
  const { homeOfficeRatio, mobileWorkingWeekdays } = useHomeOffice(sub);

  const { mutateAsync: updateUserSettings } = useRemoteSetUserSettings();

  const handleToggle = (event: React.MouseEvent<HTMLElement>, newToggle: string) => {
    setToggle(newToggle);
  };

  const postSettings = () => {
    updateUserSettings(settings)
      .then(() => {
        setOpen(false);
        setLocationOpen(false);
        dispatch(setRequireSettings({ requireSettings: false }));
        enqueueSnackbar(t("Success"), { variant: "success" });
      })
      .catch(() => {
        enqueueSnackbar(t("There has been a fatal error."), { variant: "error" });
      });
  };

  useEffect(() => {
    !settings.preferredLocations?.length && setLocationOpen(true);
  }, [settings.preferredLocations]);

  useEffect(() => {
    getLocationsWithoutMobileWorking(false, true, true)
      .then(res => {
        setLocations(res.data);
      })
      .catch(() => {
        enqueueSnackbar(t("There has been a fatal error."), { variant: "error" });
      });
  }, []);

  const workingDays = WorkingDays(mobileWorkingWeekdays);

  const mobileWorkingAllowedText = (v: boolean) =>
    v ? t("_bookingAllowed") : t("_bookingNotAllowed");

  const mobileWorkingDaysButtons =
    mobileWorkingWeekdays &&
    Object.entries(mobileWorkingWeekdays).map(([k, v]) => {
      if (k) {
        return (
          <Tooltip
            key={k}
            enterTouchDelay={0}
            title={<Typography>{mobileWorkingAllowedText(v)}</Typography>}
            aria-label={mobileWorkingAllowedText(v)}
          >
            <ToggleButton
              disableRipple
              style={{ cursor: "inherit" }}
              value="auto"
              selected={!!v} // because in some cases the value is string
              aria-label="left aligned"
            >
              {t(k)}
            </ToggleButton>
          </Tooltip>
        );
      }
    });

  const defaultStartTime = useMemo(
    () => DateTime.fromISO(settings.standardStartTime).toFormat(timeFormat),
    [timeFormat, settings.standardStartTime]
  );
  const defaultEndTime = useMemo(
    () => DateTime.fromISO(settings.standardEndTime).toFormat(timeFormat),
    [timeFormat, settings.standardEndTime]
  );

  return (
    <>
      <Fade in={true}>
        <Paper
          data-testid="booking-settings-view-parent-paper"
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            border: `1px solid black`,
            flexWrap: "wrap",
            width: "fit-content"
          }}
        >
          <ToggleButtonGroup
            size="small"
            sx={{
              display: "flex",
              flexWrap: flexWrap,
              justifyContent: "center",
              alignItems: "stretch",
              "& button": {
                margin: 0.4,
                border: "none",
                "&:not(:first-of-type)": {
                  borderRadius: 1
                },
                "&:first-of-type": {
                  borderRadius: 1
                }
              }
            }}
            value={toggle}
            exclusive
            onChange={handleToggle}
            aria-label="text alignment"
          >
            {/* default bookable Locations */}
            <Tooltip
              enterTouchDelay={0}
              title={<Typography>{t("_defaultLocations")}</Typography>}
              aria-label={"_defaultLocations"}
            >
              <ToggleButton
                onClick={() => setLocationOpen(true)}
                value="auto"
                selected={automatedBookingActivated}
                aria-label="left aligned"
                data-testid="bookable-locations"
              >
                <Apartment style={{ marginRight: 5 }} />
                {locations
                  .filter(location => settings?.preferredLocations?.includes(String(location.id)))
                  .map(location => location.name)
                  .join(", ")}
              </ToggleButton>
            </Tooltip>

            <Grid2 sx={{ display: "contents" }}>
              {/* standard bookingtime */}
              <Tooltip
                enterTouchDelay={0}
                title={<Typography>{t("_standardBookingTime")}</Typography>}
                aria-label={"_standardBookingTime"}
              >
                <ToggleButton
                  onClick={() => setOpen(true)}
                  data-testid={"open-standard-booking-times-dialog"}
                  value="auto"
                  selected={automatedBookingActivated}
                  aria-label="left aligned"
                  sx={{ minWidth: "25%" }}
                >
                  <AccessTime style={{ marginRight: 5 }} />
                  {defaultStartTime} - {defaultEndTime}
                </ToggleButton>
              </Tooltip>

              {/* automated-booking-activate */}
              {workplaceBookingEnabled && bookingAssistenceActive && (
                <Tooltip
                  enterTouchDelay={0}
                  title={<Typography>{t("_automatedBookingAssistantTitle")}</Typography>}
                  aria-label={"_automatedBookingAssistantTitle"}
                >
                  <ToggleButton
                    onClick={() => setAutomatedOpen(true)}
                    value="auto"
                    selected={automatedBookingActivated}
                    aria-label="left aligned"
                    data-testid={"open-automated-booking-activate-dialog"}
                  >
                    <Assistant />
                  </ToggleButton>
                </Tooltip>
              )}

              {/* calendar sync and integrations */}
              {calendarSyncPossible && (
                <Tooltip
                  enterTouchDelay={0}
                  title={<Typography>{t("_calendarSyncAndIntegrationsTitle")}</Typography>}
                  aria-label={"_calendarSyncAndIntegrationsTitle"}
                >
                  <ToggleButton
                    onClick={() => setCalendarSyncOpen(true)}
                    value="auto"
                    selected={calendarSyncPossible}
                    aria-label="left aligned"
                    data-testid={"open-calendarSync-dialog"}
                  >
                    <IntegrationInstructions />
                  </ToggleButton>
                </Tooltip>
              )}
            </Grid2>

            {/* hoursLeft HomeOffice */}
            {homeOfficeRatio.minMobileWorkingBookingHours +
              homeOfficeRatio.mobileWorkingBookingHours >
              0 && (
              <Tooltip
                enterTouchDelay={0}
                title={<Typography>{t("_hoursLeftHomeOffice")}</Typography>}
                aria-label={"_hoursLeftHomeOffice"}
              >
                <span style={{ alignContent: "center" }}>
                  <ToggleButton
                    sx={{ border: "inherit !important", color: "inherit !important" }}
                    disabled
                    value="hoursLeftHomeOffice"
                  >
                    <Box
                      data-testid="hoursLeftHomeOffice-container"
                      sx={{
                        width: "fit-content",
                        alignItems: "center",
                        display: "flex",
                        gap: 1,
                        mr: "3px"
                      }}
                    >
                      <Typography sx={{ width: "max-content", textTransform: "none" }}>
                        {homeOfficePolicyText(homeOfficeRatio)}
                      </Typography>
                      <Devices />
                    </Box>
                  </ToggleButton>
                </span>
              </Tooltip>
            )}

            {/* working days button group */}
            <Grid2 sx={{ display: "contents", alignSelf: "center" }}>
              {workingDays && mobileWorkingWeekdays && mobileWorkingDaysButtons}
            </Grid2>
          </ToggleButtonGroup>
        </Paper>
      </Fade>

      {/* standard booking time dialog */}
      <Dialog
        aria-labelledby="simple-dialog-title"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { backgroundImage: "none" } } }}
      >
        <DialogHeaderWithClose
          title={t("_standardBookingTime")}
          onClose={() => setOpen(false)}
          titleId="simple-dialog-title"
          closeTestId="default-bookingTime-picker-dialogContent-close"
        />
        <SeeditDialogContent data-testid="default-bookingTime-picker-dialogContent">
          <Grid2
            container
            data-testid="default-bookingTime-picker-container"
            sx={{ justifyContent: "center" }}
          >
            <Grid2 container sx={{ justifyContent: "center" }}>
              <Grid2
                data-testid="default-bookingTime-start-picker"
                sx={{ maxWidth: "fit-content" }}
              >
                <Datepicker
                  controls={["time"]}
                  timeFormat={timeFormat}
                  data-testid="default-start-picker"
                  onChange={ev => {
                    const val = DateTime.fromJSDate(ev.value as Date);
                    val &&
                      dispatch(
                        setSettings({
                          settings: {
                            ...settings,
                            standardStartTime: val.toFormat("HH:mm")
                          }
                        })
                      );
                  }}
                  value={DateTime.fromISO(
                    `${DateTime.now().toFormat("yyyy-MM-dd")}T${settings.standardStartTime}`
                  ).toFormat("HH:mm")}
                  stepMinute={5}
                  label={t("Standard Booking Start")}
                  locale={locale[i18n.language]}
                />
              </Grid2>
              <Grid2 data-testid="default-bookingTime-end-picker" sx={{ maxWidth: "fit-content" }}>
                <Datepicker
                  controls={["time"]}
                  timeFormat={timeFormat}
                  data-testid="default-end-picker"
                  onChange={ev => {
                    const val = DateTime.fromJSDate(ev.value as Date);
                    val &&
                      dispatch(
                        setSettings({
                          settings: {
                            ...settings,
                            standardEndTime: val.toFormat("HH:mm")
                          }
                        })
                      );
                  }}
                  value={DateTime.fromISO(
                    `${DateTime.now().toFormat("yyyy-MM-dd")}T${settings.standardEndTime}`
                  ).toFormat("HH:mm")}
                  stepMinute={5}
                  label={t("Standard Booking End")}
                  locale={locale[i18n.language]}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </SeeditDialogContent>
        <SeeditDialogActions data-testid="default-bookingTime-confirm">
          <Button
            data-testid="postSettings"
            disabled={(settings?.preferredLocations?.length ?? 0) <= 0}
            onClick={postSettings}
          >
            {t("Confirm")}
          </Button>
        </SeeditDialogActions>
      </Dialog>

      {/* booking setting - location select and preferred category select */}
      <Dialog
        aria-labelledby="bookable-locations-dialog-title"
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
      >
        <DialogHeaderWithClose
          title={t("Bookable Locations")}
          onClose={() => setLocationOpen(false)}
          titleId="bookable-locations-dialog-title"
          closeTestId="bookingSetting-location-preferredCategory-close"
        />
        <SeeditDialogContent>
          <Grid2
            data-testid="bookingSetting-location-preferredCategory-combo-grid"
            sx={{ margin: "10px 0 10px 0", minWidth: "20vw", flexDirection: "column" }}
            container
            gap={3}
          >
            <LocationCombo placeholder={t("Location")} />
          </Grid2>
        </SeeditDialogContent>
      </Dialog>

      {/* automated booking dialog */}
      <Dialog
        aria-labelledby="automated-booking-dialog-title"
        open={automatedOpen}
        onClose={() => setAutomatedOpen(false)}
        slotProps={{ paper: { sx: { maxWidth: 465 } } }}
      >
        <DialogHeaderWithClose
          title={
            <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
              <Typography component="span" variant="h6">
                {t("_automatedBookingAssistantTitle")}
              </Typography>
              <InfoIconWithTooltip tooltipText={t("_automatedBookingAssistantInfo")} />
            </Box>
          }
          onClose={() => setAutomatedOpen(false)}
          titleId="automated-booking-dialog-title"
          closeTestId="close-automatedBookingAssistant-btn"
        />
        <AutomatedBookingRoutine setAutomatedOpen={setAutomatedOpen} />
      </Dialog>

      {/* calendar sync and integration dialog */}
      <Dialog
        aria-labelledby="calendar-sync-dialog-title"
        open={calendarSyncOpen}
        onClose={() => setCalendarSyncOpen(false)}
        slotProps={{ paper: { sx: { maxWidth: 650 } } }}
      >
        <DialogHeaderWithClose
          title={t("_calendarSyncAndIntegrationsTitle")}
          onClose={() => setCalendarSyncOpen(false)}
          titleId="calendar-sync-dialog-title"
          closeTestId="btn-group-close"
        />
        <CalendarSyncIntegrationBooking setCalendarSyncOpen={setCalendarSyncOpen} />
      </Dialog>
    </>
  );
}
